// React
import { useCallback, useEffect, useMemo, useState } from 'react';

// UI primitives
import { Toaster } from '@/components/ui/toaster';

// Shared components
import { Page } from '../layouts/Page';
import { PageHeaderTotalLabel, PageHeaderSearchBar } from '../layouts/PageHeader';
import { ResponsivePageHeader } from '../layouts/ResponsivePageHeader';
import { openMobileSidebar } from '../MobileSidebar/mobileSidebarEvents';
import { DataTableFooter } from '@components/DataTable/DataTableFooter';
import { AllProductsPage } from '@components/Product/Page/AllProductsPage';
import type { ProductCategoryOptions } from '@components/Product/categories';
import { ProductDetailSheet } from '@components/Product/Sections/ProductDetailSheet';

// Local components
import { CreateProductDialog } from './CreateProductDialog';

// Hooks and utilities
import { useToast } from '@/hooks/use-toast';
import { useProductRouterSearch } from '@hooks/useProductRouterSearch';
import { useProducts, useProductFilters } from '@hooks/products';
import { searchQueryParam } from '@/react/utils/searchQuery';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useTranslation } from '@hooks/useTranslation';
import { useFeatureFlag } from '@hooks/useFeatures';
import { SEARCH_BAR_WIDTH } from '../../constants';

// RBAC
import { can, CAPABILITIES, type CurrentUser } from '../../lib/capabilities';

// Types
import type { CommittedProductFilters, ProductFilterDismissHandlers, ProductTranslations } from '@components/Product/types';

interface ProductManagementProps {
  translations?: ProductTranslations;
  currentUser?: CurrentUser;
  /** Server-defined category vocabulary — feeds the create modal
   *  and the list's category filter. */
  categoryOptions: ProductCategoryOptions;
}

export default function ProductManagement({ translations: _translations, currentUser, categoryOptions }: ProductManagementProps) {
  const { t } = useTranslation();
  const canWrite = can(currentUser, CAPABILITIES.PRODUCTS_WRITE);
  const showBcrsDeposit = useFeatureFlag('bcrs_deposit');
  const showDisplayCopy = useFeatureFlag('display_attachment_upload');
  const router = useProductRouterSearch();
  const { search } = router;

  const [searchInput, setSearchInput] = useState(search.q);
  const debouncedQuery = useDebouncedValue(searchInput);

  // Re-sync local input when URL changes externally (popstate / in-router nav)
  useEffect(() => {
    setSearchInput(search.q);
  }, [search.q]);

  // Sync debounced typing back to URL
  useEffect(() => {
    if (debouncedQuery !== search.q) {
      router.setQuery(debouncedQuery);
    }
  }, [debouncedQuery, search.q, router]);

  // Each key names the URL field it binds, so a crossed pair is visible here
  // rather than hidden in an argument's position.
  const filters = useProductFilters({
    catalogueIds: { value: search.catalogueIds, commit: router.setCatalogueIds },
    dietTags: { value: search.dietTags, commit: router.setDietTags },
    allergenTags: { value: search.allergenTags, commit: router.setAllergenTags },
    categories: { value: search.categories, commit: router.setCategories },
    nutritionTags: { value: search.nutritionTags, commit: router.setNutritionTags },
    chefPick: { value: search.chefPick, commit: router.setChefPick },
    servingTemperatures: { value: search.servingTemperatures, commit: router.setServingTemperatures },
    statuses: { value: search.statuses, commit: router.setStatuses },
    formats: { value: search.formats, commit: router.setFormats },
    allCategories: categoryOptions.all,
  });

  // Committed filter state — reflects what's actually in the URL (and thus
  // the query). `filters.*` tracks the modal's pending state, so we don't
  // read from it for chip rendering or the badge count; otherwise chip
  // dismissals (which bypass the modal's apply) wouldn't update the UI.
  const committedFilters = useMemo<CommittedProductFilters>(
    () => ({
      formats: search.formats,
      statuses: search.statuses,
      catalogueIds: search.catalogueIds,
      categories: search.categories,
      dietTags: search.dietTags,
      allergenTags: search.allergenTags,
      nutritionTags: search.nutritionTags,
      chefPick: search.chefPick,
      servingTemperatures: search.servingTemperatures,
    }),
    [
      search.formats,
      search.statuses,
      search.catalogueIds,
      search.categories,
      search.dietTags,
      search.allergenTags,
      search.nutritionTags,
      search.chefPick,
      search.servingTemperatures,
    ],
  );

  const committedFilterCount =
    committedFilters.formats.length +
    committedFilters.statuses.length +
    committedFilters.catalogueIds.length +
    committedFilters.categories.length +
    committedFilters.dietTags.length +
    committedFilters.allergenTags.length +
    committedFilters.nutritionTags.length +
    (committedFilters.chefPick ? 1 : 0) +
    committedFilters.servingTemperatures.length;

  const dismissHandlers = useMemo<ProductFilterDismissHandlers>(
    () => ({
      removeFormat: (form) =>
        router.setFormats(search.formats.filter((f) => f !== form)),
      removeStatus: (status) =>
        router.setStatuses(search.statuses.filter((s) => s !== status)),
      removeCatalogue: (id) =>
        router.setCatalogueIds(search.catalogueIds.filter((x) => x !== id)),
      removeCategory: (category) =>
        router.setCategories(search.categories.filter((c) => c !== category)),
      removeDietTag: (tag) =>
        router.setDietTags(search.dietTags.filter((t) => t !== tag)),
      removeAllergenTag: (tag) =>
        router.setAllergenTags(search.allergenTags.filter((t) => t !== tag)),
      removeNutritionTag: (tag) =>
        router.setNutritionTags(search.nutritionTags.filter((t) => t !== tag)),
      removeChefPick: () => router.setChefPick(false),
      removeServingTemperature: (temp) =>
        router.setServingTemperatures(
          search.servingTemperatures.filter((t) => t !== temp),
        ),
    }),
    [router, search.formats, search.statuses, search.catalogueIds, search.categories, search.dietTags, search.allergenTags, search.nutritionTags, search.servingTemperatures],
  );

  const { data, isLoading, isFetching } = useProducts({
    status: search.status,
    active_status: search.statuses.length > 0 ? search.statuses : undefined,
    forms: search.formats.length > 0 ? search.formats : undefined,
    q: searchQueryParam(debouncedQuery),
    page: search.page,
    perPage: search.perPage,
    sortBy: search.sort || undefined,
    sortDirection: search.direction || undefined,
    catalogueId: search.catalogueIds.length > 0 ? search.catalogueIds : undefined,
    dietTags: search.dietTags.length > 0 ? search.dietTags : undefined,
    allergenTags: search.allergenTags.length > 0 ? search.allergenTags : undefined,
    category: search.categories.length > 0 ? search.categories : undefined,
    nutritionTags: search.nutritionTags.length > 0 ? search.nutritionTags : undefined,
    chefRecommended: search.chefPick || undefined,
    servingTemperature: search.servingTemperatures.length > 0 ? search.servingTemperatures : undefined,
  });

  const products = data?.products ?? [];
  const totalCount = data?.pagination.total ?? 0;
  const totalUnfiltered = data?.totalUnfiltered ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / search.perPage));

  // Create product dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Product detail sheet state
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);

  const selectedProduct =
    products.find((p) => p.id === selectedProductId) ?? null;

  // Close the sheet if the selected product disappears from the list (e.g., after filter change)
  useEffect(() => {
    if (selectedProductId !== null && !selectedProduct) {
      setSelectedProductId(null);
    }
  }, [selectedProductId, selectedProduct]);

  const { dismiss: dismissToasts } = useToast();

  const handleProductSelect = useCallback(
    (product: { id: number }) => {
      dismissToasts();
      setSelectedProductId(product.id);
    },
    [dismissToasts]
  );

  return (
    <>
      <Toaster />
      <Page>
        <Page.Header className="py-6 md:py-0">
          <ResponsivePageHeader
            mobile={{
              title: t('header.title'),
              count: isLoading ? '…' : totalCount.toLocaleString(),
              searchValue: searchInput,
              onSearchChange: setSearchInput,
              searchPlaceholder: t('search.placeholder'),
              searchAriaLabel: t('search.aria_label'),
              openNavigationAriaLabel: t('header.open_navigation'),
              openSearchAriaLabel: t('header.open_search'),
              closeSearchAriaLabel: t('header.close_search'),
              onOpenNavigation: openMobileSidebar,
            }}
          >
            <div className="shrink-0">
              <PageHeaderTotalLabel
                count={totalUnfiltered}
                label={t('header.total_product')}
              />
            </div>
            <div
              className="flex-1 min-w-0 flex justify-center"
              style={{ flexBasis: SEARCH_BAR_WIDTH }}
            >
              <PageHeaderSearchBar
                value={searchInput}
                onChange={setSearchInput}
                placeholder={t('search.placeholder')}
                ariaLabel={t('search.aria_label')}
              />
            </div>
            <div
              className="min-w-0 invisible"
              aria-hidden="true"
              style={{ flexShrink: 99 }}
            >
              <PageHeaderTotalLabel
                count={totalUnfiltered}
                label={t('header.total_product')}
              />
            </div>
          </ResponsivePageHeader>
        </Page.Header>
        <Page.Body>
          <AllProductsPage
            currentStatus={search.status}
            totalCount={totalCount}
            onStatusChange={router.setStatus}
            isLoading={isLoading}
            isFetching={isFetching}
            products={products}
            sort={search.sort}
            direction={search.direction}
            onSortChange={router.setSort}
            onRowClick={handleProductSelect}
            onNewProductClick={() => setCreateDialogOpen(true)}
            filters={filters}
            filterCount={committedFilterCount}
            committedFilters={committedFilters}
            dismissHandlers={dismissHandlers}
            canWrite={canWrite}
            categoryOptions={categoryOptions}
          />
        </Page.Body>
        <Page.Footer>
          <DataTableFooter
            currentPage={Math.min(search.page, totalPages)}
            totalPages={totalPages}
            totalCount={totalCount}
            perPage={search.perPage}
            onPageChange={router.setPage}
            onPerPageChange={router.setPerPage}
            className="flex flex-col justify-center h-full px-6 sm:px-8 lg:px-16"
          />
        </Page.Footer>
      </Page>

      <ProductDetailSheet
        product={selectedProduct}
        open={selectedProductId !== null}
        onOpenChange={(open) => { if (!open) setSelectedProductId(null); }}
        canWrite={canWrite}
        showBcrsDeposit={showBcrsDeposit}
        showDisplayCopy={showDisplayCopy}
      />

      {/* RBAC: the create dialog is reachable only via the New Product button,
          which is hidden for read-only users — but gate its mount too. */}
      {canWrite && (
        <CreateProductDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          categoryOptions={categoryOptions}
        />
      )}
    </>
  );
}
